/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsModule
 */

import { L10nService } from '@vmw/ngx-vip';
import { DnsRuleAction } from 'object-types';
import { IRateLimiter } from 'generated-types';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { StringService } from 'string-service';
import * as globalL10n from 'global-l10n';
import * as l10n
    // eslint-disable-next-line max-len
    from 'ng/lazy-loaded-components/modals/dns-policy-modal/components/actions/dns-policy-action/dns-policy-action.l10n';
import { PolicyActionConfigItem }
    from '../../policies/factories/config-items/policy-action.config-item.factory';
import { IExtendedDnsRuleAction } from './dns-policy.item.types';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Types of Dns Rule Actions.
 */
export enum DnsRuleActionType {
    ALLOW = 'allow',
    DNS_RATE_LIMITER = 'dns_rate_limiter',
    GSLB_SITE_SELECTION = 'gslb_site_selection',
    POOL_SWITCHING = 'pool_switching',
    RESPONSE = 'response',
    GS_GROUP_SELECTION = 'gs_group_selection',
}

/**
 * Map to get label for Action readonly(expander) text.
 * The values are same as the Radio Button labels of Action Component.
 */
const fieldAndActionLabelMap = new Map([
    [DnsRuleActionType.ALLOW, l10nKeys.allowDropQueryLabel],
    [DnsRuleActionType.RESPONSE, l10nKeys.queryResponseLabel],
    [DnsRuleActionType.DNS_RATE_LIMITER, l10nKeys.rateLimitLabel],
    [DnsRuleActionType.GSLB_SITE_SELECTION, globalL10nKeys.gslbSiteLabel],
    [DnsRuleActionType.POOL_SWITCHING, globalL10nKeys.poolSwitchingLabel],
    [DnsRuleActionType.GS_GROUP_SELECTION, globalL10nKeys.gsGroupSelectionLabel],
]);

/**
 * @description DnsRuleAction MessageItem class.
 * @author Abhinesh Gour
 */
export class DnsRuleActionConfigItem extends PolicyActionConfigItem<IExtendedDnsRuleAction> {
    public static ajsDependencies = [
        'schemaService',
        'stringService',
        'l10nService',
    ];

    /**
     * Global Keys from source bundles.
     */
    public globalL10nKeys = globalL10nKeys;

    private readonly schemaService: SchemaService;
    private readonly stringService: StringService;
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        super({
            objectType: DnsRuleAction,
            ...args,
        });

        this.schemaService = this.getAjsDependency_('schemaService');
        this.stringService = this.getAjsDependency_('stringService');
        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Called on change of action type. Delete selected pool / pool group.
     */
    public onPoolSwitchingTypeChange(): void {
        const { config: { pool_switching: { config: poolSwitchConfig } } } = this;

        delete poolSwitchConfig.pool_ref;
        delete poolSwitchConfig.pool_group_ref;
    }

    /**
     * Convenience method to get fields for DNS Rule Action.
     */
    public get dnsRuleActionFields(): string[] {
        return this.schemaService.getObjectFieldNames(DnsRuleAction);
    }

    /**
     * Returns DNS Rule Action type.
     */
    public get dnsActionType(): DnsRuleActionType {
        const {
            allow,
            dns_rate_limiter: dnsRateLimiter,
            gslb_site_selection: gslbSiteSelection,
            pool_switching: poolSwitching,
            response,
            gs_group_selection: gsGroupSelection,
        } = this.getConfig();

        if (allow) {
            return DnsRuleActionType.ALLOW;
        }

        if (dnsRateLimiter) {
            return DnsRuleActionType.DNS_RATE_LIMITER;
        }

        if (gslbSiteSelection) {
            return DnsRuleActionType.GSLB_SITE_SELECTION;
        }

        if (poolSwitching) {
            return DnsRuleActionType.POOL_SWITCHING;
        }

        if (response) {
            return DnsRuleActionType.RESPONSE;
        }

        if (gsGroupSelection) {
            return DnsRuleActionType.GS_GROUP_SELECTION;
        }

        return this.createDefualtActionType();
    }

    /**
     * Create new child field and return ActionType for default scenario.
     */
    public createDefualtActionType(): DnsRuleActionType {
        this.safeSetNewChildByField('allow');

        return DnsRuleActionType.ALLOW;
    }

    /**
     * Event handler for ActionType change event.
     */
    public onActionTypeChange(ruleActionType: string): void {
        const config = this.getConfig();

        for (const fieldName of this.dnsRuleActionFields) {
            if (fieldName !== ruleActionType) {
                delete config[fieldName];
            }
        }

        switch (ruleActionType) {
            case DnsRuleActionType.ALLOW:
                this.safeSetNewChildByField('allow');
                break;

            case DnsRuleActionType.DNS_RATE_LIMITER:
                this.safeSetNewChildByField('dns_rate_limiter');
                break;

            case DnsRuleActionType.GSLB_SITE_SELECTION:
                this.safeSetNewChildByField('gslb_site_selection');
                break;

            case DnsRuleActionType.POOL_SWITCHING:
                this.safeSetNewChildByField('pool_switching');
                break;

            case DnsRuleActionType.RESPONSE:
                this.safeSetNewChildByField('response');
                break;

            case DnsRuleActionType.GS_GROUP_SELECTION:
                this.safeSetNewChildByField('gs_group_selection');
                break;
        }
    }

    /**
     * Returns DnsRuleAction label for action header.
     */
    public get dnsRuleActionLabel(): string {
        if (!this.dnsActionType) {
            return '';
        }

        return fieldAndActionLabelMap.get(this.dnsActionType);
    }

    /**
     * Returns Generic RateLimiter Message Item Config.
     */
    public get rateLimiterConfig(): IRateLimiter {
        return this.config.dns_rate_limiter.config.rate_limiter_object.config;
    }

    /**
     * Returns DnsRuleAction Type in human readable text format.
     * For IDnsRuleAction, action types are Message items and not Enums.
     * Currently showing readonly information as it was in old config.
     * TODO: Need to revisit, if there are any possible changes suggested.
     */
    public get dnsRuleActionText(): string {
        if (!this.dnsActionType) {
            return '';
        }

        let actionText = '';

        switch (this.dnsActionType) {
            case DnsRuleActionType.ALLOW: {
                const { allow, reset_conn: resetConn } = this.config.allow.config;

                actionText += `${allow ?
                    `${this.l10nService.getMessage(this.globalL10nKeys.allowLabel)} ` :
                    `${this.l10nService.getMessage(this.globalL10nKeys.dropLabel)} `}
                    ${this.l10nService.getMessage(this.globalL10nKeys.queryLabel)}`;

                if (resetConn) {
                    actionText +=
                        `; ${this.l10nService.getMessage(l10nKeys.resetTcpConnectionLabel)}`;
                }

                break;
            }

            case DnsRuleActionType.RESPONSE: {
                const { rcode, truncation, authoritative } = this.config.response.config;
                const outputText = [];

                if (rcode) {
                    const rCode = rcode.slice('DNS_RCODE_'.length).toUpperCase();

                    outputText.push(
                        `${this.l10nService.getMessage(l10nKeys.responseCodeLabel)}:
                            "${rCode}"`,
                    );
                }

                if (truncation) {
                    outputText.push(
                        `${this.l10nService.getMessage(l10nKeys.truncationLabel)}:
                            ${this.l10nService.getMessage(this.globalL10nKeys.trueLabel)}`,
                    );
                }

                if (authoritative) {
                    outputText.push(
                        `${this.l10nService.getMessage(l10nKeys.authoritativeLabel)}:
                            ${this.l10nService.getMessage(this.globalL10nKeys.trueLabel)}`,
                    );
                }

                actionText = outputText.join('; ');
                break;
            }

            case DnsRuleActionType.POOL_SWITCHING: {
                const {
                    pool_ref: pool,
                    pool_group_ref: poolGroup,
                } = this.config.pool_switching.config;

                const passthroughLabelText =
                    this.l10nService.getMessage(this.globalL10nKeys.passthroughIsServedByLabel);

                let poolOrPoolGroup = '';

                if (pool) {
                    poolOrPoolGroup = this.l10nService.getMessage(this.globalL10nKeys.poolLabel);
                }

                if (poolGroup) {
                    poolOrPoolGroup =
                        this.l10nService.getMessage(this.globalL10nKeys.poolGroupLabel);
                }

                const poolGroupName = poolGroup ? this.stringService.name(poolGroup) : '';
                const poolOrPoolGroupName = pool ? this.stringService.name(pool) : poolGroupName;

                actionText =
                    `${passthroughLabelText} ${poolOrPoolGroup} : ${poolOrPoolGroupName}`;
                break;
            }

            case DnsRuleActionType.DNS_RATE_LIMITER: {
                const {
                    burst_sz: burstSize,
                    count,
                    period,
                } = this.config.dns_rate_limiter.config.rate_limiter_object.config;

                const { type } = this.config.dns_rate_limiter.config.action.config;

                const outputs = [];

                if (type) {
                    outputs.push(
                        `${this.l10nService.getMessage(this.globalL10nKeys.actionLabel)}:
                            ${this.schemaService.getEnumValueLabel('DnsRuleRLActionType', type)}`,
                    );
                }

                if (count) {
                    outputs.push(
                        `${this.l10nService.getMessage(this.globalL10nKeys.countLabel)}: ${count}`,
                    );
                }

                if (burstSize) {
                    outputs.push(
                        `${this.l10nService.getMessage(this.globalL10nKeys.burstSizeLabel)}:
                            ${burstSize}`,
                    );
                }

                if (period) {
                    outputs.push(`
                        ${this.l10nService.getMessage(this.globalL10nKeys.periodLabel)}:
                            ${period}`);
                }

                actionText = outputs.join('; ');
                break;
            }

            case DnsRuleActionType.GSLB_SITE_SELECTION: {
                const { site_name: siteName } = this.config.gslb_site_selection.config;

                actionText = siteName;
                break;
            }

            case DnsRuleActionType.GS_GROUP_SELECTION: {
                const { group_name: groupName } = this.config.gs_group_selection.config;

                if (groupName) {
                    actionText = `${this.l10nService.getMessage(l10nKeys.groupNameLabel)}:
                        ${groupName}`;
                }

                break;
            }
        }

        return actionText;
    }
}
